import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { login, setToken } from "@/lib/api";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1760326652193-bc25f0fe4a8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3p5JTIwY2FmZSUyMGNvdW50ZXIlMjBzdW5saWdodHxlbnwwfHx8fDE3NzcwMjU0MzV8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setErrorMsg("Please enter both username and password.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      const res = await login(username, password);
      if (res?.success && res?.token) {
        setToken(res.token);
        toast.success("Welcome back");
        nav("/", { replace: true });
      } else {
        setErrorMsg(res?.message || "Invalid username or password");
      }
    } catch (err) {
      setErrorMsg("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-2 bg-bone"
      data-testid="login-page"
    >
      {/* Left hero */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Warm sunlit café counter"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C3B2E]/35 via-transparent to-[#D47A6A]/20" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3 text-white">
            <span className="h-10 w-10 grid place-items-center rounded-2xl bg-white/90 text-terracotta shadow-ambient">
              <Mic className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <span className="font-display text-lg tracking-tight">
              Voice Bug Intake
            </span>
          </div>
          <div className="max-w-md text-white animate-fade-up">
            <p className="accent-label text-white/80 mb-4">Talk. We'll handle the rest.</p>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-4">
              Report bugs by simply speaking.
            </h1>
            <p className="text-white/85 text-base leading-relaxed">
              No forms to fill. No jargon. Just press record, describe the problem in
              your own words, and we'll shape it into a clean report your team can act
              on.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div
          className="w-full max-w-md bg-white border border-sage rounded-2xl shadow-ambient p-8 sm:p-10 animate-fade-up"
          data-testid="login-card"
        >
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <span className="h-10 w-10 grid place-items-center rounded-2xl bg-sand text-terracotta">
              <Mic className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <span className="font-display text-lg text-ink">Voice Bug Intake</span>
          </div>

          <p className="accent-label mb-3">Secure sign-in</p>
          <h2 className="font-display text-3xl text-ink mb-2">Welcome back</h2>
          <p className="text-ink-muted text-sm mb-8">
            Enter your credentials to start reporting.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label
                htmlFor="username"
                className="text-ink text-xs font-semibold tracking-wider uppercase"
              >
                Username
              </Label>
              <Input
                id="username"
                data-testid="login-username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your username"
                className="mt-2 h-12 rounded-xl bg-bone border-sage focus-visible:ring-terracotta focus-visible:ring-1 focus-visible:border-[#D47A6A]"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-ink text-xs font-semibold tracking-wider uppercase"
              >
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 rounded-xl bg-bone border-sage focus-visible:ring-terracotta focus-visible:ring-1 focus-visible:border-[#D47A6A]"
              />
            </div>

            {errorMsg && (
              <p
                className="text-sm rounded-xl border border-[#D47A6A]/40 bg-[#D47A6A]/10 text-[#A85346] px-4 py-3"
                data-testid="login-error"
              >
                {errorMsg}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-terracotta hover:bg-[#C06555] active:bg-[#A85346] text-white text-base font-medium transition-all duration-200"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={1.8} />
                  Signing in...
                </>
              ) : (
                <>Sign in</>
              )}
            </Button>
          </form>

          <div className="mt-8 flex items-center gap-2 text-xs text-ink-muted">
            <ShieldCheck className="w-4 h-4 text-[#7B9672]" strokeWidth={1.6} />
            Internal tool · Credentials verified server-side
          </div>
        </div>
      </div>
    </div>
  );
}
